import React from "react";
import { useHistory } from "react-router-dom";
import firebaseApi from "../../apis/firebaseApi";
import TextToggle from "../common/TextToggle";
import { FrameContext } from "../../contexts/FrameContext";
import schedulingHelpers from "../../helpers/schedulingHelpers";
import useTranslations from "../../hooks/useTranslations";
import DayScheduleContainer from "../common/DayScheduleContainer";
import dateHelpers from "../../helpers/dateHelpers";

const LocationProfile = () => {
  const { source, companyId, locationId, brandId, appointmentData, user, isInFrame, sendMessage } = React.useContext(FrameContext);
  const [agents, setAgents] = React.useState();
  const [team, setTeam] = React.useState();
  const [company, setCompany] = React.useState();

  const [bookedSchedules, setBookedSchedules] = React.useState([]);
  const [isToday, setIsToday] = React.useState(true);
  const [isValid, setIsValid] = React.useState(true);
  const [isButtonsDisabled, setButtonsDisabled] = React.useState(false);

  const isTodayRef = React.useRef();
  const isInitialLoad = React.useRef(true);
  let history = useHistory();

  let content = useTranslations();
  const [selectedRadio, setSelectedRadio] = React.useState(content.scheduleAppointment.todayButton);

  async function asyncForEach(array, callback) {
    for (let index = 0; index < array.length; index++) {
      await callback(array[index], index, array);
    }
  }
  async function validateAgent(agentDoc, accountKey, subsiteId) {
    if (agentDoc.ids) {
      let insideId = agentDoc.ids.inside;
      if (insideId) {
        let response = await fetch(agentDoc.baseUrl + "/api/chatlinks/validateOperatorLinks", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accountKey, operatorId: insideId, subsiteId }),
        });
        if (response && response.ok) {
          const res = await response.json();
          console.log(`opId = ${insideId}, response = ${JSON.stringify(res)}`);
          return res.valid;
        }
      }
    }
    return false;
  }

  async function validateAgentsBatch(agentDocs, accountKey, subsiteId) {
    if (!agentDocs || agentDocs.length === 0) return [];

    // All agents should have the same baseUrl, so we use the first one
    const baseUrl = agentDocs[0].baseUrl;
    const operatorIds = agentDocs.filter((doc) => doc.ids && doc.ids.inside).map((doc) => doc.ids.inside);

    if (operatorIds.length === 0) return [];

    try {
      let response = await fetch(baseUrl + "/api/chatlinks/validateOperatorLinks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accountKey, operatorIds, subsiteId }),
      });

      if (response && response.ok) {
        const results = await response.json();
        // If response is an array, we're using new API version
        if (Array.isArray(results)) {
          return results.map((res) => res.valid);
        }
        // If response is object with single result, we're using old API version
        // Fall back to individual validation
        return [results.valid];
      }
    } catch (error) {
      console.error("Error in batch validation:", error);
    }

    // If batch validation fails, return empty array to trigger fallback
    return [];
  }

  React.useEffect(() => {
    if (!companyId || !locationId) return;
    setIsToday(true);
    isTodayRef.current = true;
    isInitialLoad.current = true;

    firebaseApi.getAgents(source.source, companyId, locationId, async (agentSnaps) => {
      let items = [];
      let agentDocs = [];

      agentSnaps.forEach(async (x) => {
        let agentDoc = { ...x.data(), id: x.id };
        let agentValid = agentDoc.isActive !== false && agentDoc.workHours && agentDoc.workHours.data;
        if (agentValid) agentDocs.push(agentDoc);
      });

      if (source.source === "inside") {
        // Try batch validation first
        const batchResults = await validateAgentsBatch(agentDocs, source.companyId, source.brandId);

        if (batchResults.length === agentDocs.length) {
          // Batch validation successful, use results
          agentDocs.forEach((doc, index) => {
            if (batchResults[index]) {
              items.push(doc);
            }
          });
        } else {
          // Fall back to individual validation if batch validation failed or returned unexpected results
          await asyncForEach(agentDocs, async (agentDoc) => {
            let agentValid = await validateAgent(agentDoc, source.companyId, source.brandId);
            if (agentValid) items.push(agentDoc);
          });
        }
      } else {
        // For non-inside source, add all valid agents
        items = [...agentDocs];
      }

      setAgents(items);
    });

    firebaseApi.getPendingLocationSchedules(companyId, locationId, (data) => {
      let items = [];
      data.forEach((x) => items.push({ ...x.data(), id: x.id }));
      setBookedSchedules(items);
    });

    firebaseApi.getLocation(companyId, locationId, (locationSnap) => {
      setTeam({ ...locationSnap.data(), id: locationSnap.id });
    });
    firebaseApi.getCompany(source.source, source.companyId).then((company) => {
      setCompany({ ...company.data(), id: company.id });
    });
  }, [source, companyId, locationId]);

  const checkSchedule = (agentId, schedule, callback) => {
    // First check if the selected schedule is not part of their unavailability.
    let agent = agents.find((x) => x.id === agentId);
    const scheduleStartTime = new Date(
      new Date(schedule.schedule.startTime * 1000).getFullYear(),
      new Date(schedule.schedule.startTime * 1000).getMonth(),
      new Date(schedule.schedule.startTime * 1000).getDate(),
      0,
      0,
      0,
      0
    );
    const scheduleEndTime = new Date(
      new Date(schedule.schedule.endTime * 1000).getFullYear(),
      new Date(schedule.schedule.endTime * 1000).getMonth(),
      new Date(schedule.schedule.endTime * 1000).getDate(),
      0,
      0,
      0,
      0
    );

    // If the schedule lies in the unavailbleSchedule of the agent then return false and select different agent.
    if (
      agent &&
      agent.settings &&
      agent.settings.unavailableSchedule &&
      agent.settings.unavailableSchedule.length > 0 &&
      agent.settings.unavailableSchedule.some((y) => scheduleStartTime >= new Date(y.startDate * 1000) && scheduleEndTime <= new Date(y.endDate * 1000))
    ) {
      callback(false);
      return;
    }

    firebaseApi.getPendingAgentSchedules(companyId, agentId, (s) => {
      let isValid = true;
      s.forEach((x) => {
        let appointment = x.data();
        if (appointment.schedule.startTime >= schedule.schedule.startTime && appointment.schedule.endTime <= schedule.schedule.endTime) {
          isValid = false;
        }
      });

      callback(isValid);
    });
  };

  const onScheduleCallClick = (schedule, count) => {
    if ((count || 0) > 3) {
      setIsValid(false);
      return;
    }
    setIsValid(true);
    setButtonsDisabled(true);
    let randomAgentId = Math.floor(Math.random() * Math.floor(schedule.agents.length));
    let agent = schedule.agents[randomAgentId];
    source.agentId = agent.ids[source.source];

    checkSchedule(agent.id, schedule, function (isValid) {
      if (isValid) {
        let data = {
          agentId: agent.id || null,
          locationId: locationId,
          schedule: schedule.schedule,
          user: user,
          createdDateTime: Math.floor(Date.now() / 1000),
        };
        if (agent) {
          if (agent.name) data.name = agent.name;
          if (agent.slug) data.slug = agent.slug;
        }
        if (brandId) data.brandId = brandId;
        if (appointmentData) data.appointmentData = appointmentData;

        firebaseApi.createSchedule(companyId, data, (response) => {
          if (!response.success && response.error) {
            alert(response.error);
            setIsValid(false);
            setButtonsDisabled(false);
            return;
          } else {
            const docData = response.docData;
            firebaseApi.sendEmail(
              { ...data, source: source.source, companyId: source.companyId, brandId: brandId, scheduleId: docData.id },
              () => {
                history.push(`/appointment/${docData.id}`);
                setButtonsDisabled(false);
              },
              3
            );
          }
        });
      } else {
        onScheduleCallClick(schedule, (count || 1) + 1);
      }
    });
  };

  const onTextToggled = (isChecked, value) => {
    setSelectedRadio(value);
    setIsToday(value === content.scheduleAppointment.todayButton);
  };

  const getDays = () => {
    let availableDays = [];
    let retAvailableDays = [];
    if (selectedRadio === content.scheduleAppointment.todayButton) {
      availableDays = [new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())];
    } else if (selectedRadio === content.scheduleAppointment.thisWeekButton) {
      availableDays = schedulingHelpers.getAvailableDaysAgents(agents, 7);
    } else {
      availableDays = schedulingHelpers.getAvailableDaysAgents(agents, 7, true);
    }

    // Added the functionality of unavailability option for the location.
    // Unavailabilty can be defined in both team level and company level.
    // When searching both of these needs to be merged together.
    // We also need to add agent's unavailability here as well it is possible that one agent is available on one day and other is not on the same day.
    let unavailableSchedule = [];
    if (team && team.settings && team.settings.unavailableSchedule && team.settings.unavailableSchedule.length > 0) {
      unavailableSchedule = [...team.settings.unavailableSchedule];
    }

    if (company && company.settings && company.settings.unavailableSchedule && company.settings.unavailableSchedule.length > 0) {
      unavailableSchedule = [...unavailableSchedule, ...company.settings.unavailableSchedule];
    }

    //Also include the common unavailable schedule accross the agents.
    // Common only because there can exist a case where one agent might be available in other's holiday
    if (agents.length > 1) unavailableSchedule = [...unavailableSchedule, ...findAllCommonUnavailableSchedules()];

    if (unavailableSchedule.length > 0) {
      availableDays = availableDays.map((x) => new Date(x.getFullYear(), x.getMonth(), x.getDate(), 0, 0, 0, 0));
      retAvailableDays = availableDays.filter((x) => !unavailableSchedule.some((y) => x >= new Date(y.startDate * 1000) && x <= new Date(y.endDate * 1000)));
    } else {
      retAvailableDays = [...availableDays];
    }

    return retAvailableDays;
  };

  const findAllCommonUnavailableSchedules = () => {
    const dateMap = new Map();
    let agentsWithSettings = 0;

    // Populate dateMap with counts of dates across all agents
    agents.forEach((agent) => {
      if (agent && agent.settings && agent.settings.unavailableSchedule && agent.settings.unavailableSchedule.length > 0) {
        agentsWithSettings += 1;
        agent.settings.unavailableSchedule.forEach((schedule) => {
          for (let timestamp = schedule.startDate; timestamp <= schedule.endDate; timestamp += 86400) {
            // Increment by one day
            dateMap.set(timestamp, (dateMap.get(timestamp) || 0) + 1);
          }
        });
      }
    });

    const commonDates = [];
    const agentCount = agents.length;
    // For common dates to be ommitted, it is necessary that all the agents have unavailable schedule available. Else at least one agent will be available all the time.
    // Rest of the agent's unavailability and assignment is handled when assigning the appointment.
    if (agentCount !== agentsWithSettings) return commonDates;

    // Collect only dates that appear in all agent's schedules
    for (const [date, count] of dateMap.entries()) {
      if (count === agentCount) {
        commonDates.push({ startDate: date, endDate: date });
      }
    }

    return commonDates;
  };

  const getBrokenDownSchedules = () => {
    return schedulingHelpers.getSchedulingBreakdownAgents(agents, selectedRadio === content.scheduleAppointment.nextWeekButton);
  };

  const onCancelClick = () => {
    sendMessage("scheduling_canceled");
  };

  const getDaySchedule = (day) => {
    if (!agents.length) {
      return <div className="text-center">Sorry, no agent is available to take the appointment.</div>;
    }

    // if the day is after the DST is applied and before the DST is ended and visitor's offset != after DST start offset
    // Add +1 to the user GMT as the DST is started after that
    // if the day is after the DST is ended and visitor's offset != after the DST ends offset
    // Add -1 to the user GMT as the DST is ended after that

    // As the code is run on the visitor browser, the dst transitions will be according to their machine's timezone
    const dstTransitionDates = schedulingHelpers.getDstTransitionDates();
    let userOffset = dateHelpers.getTimeZoneOffset();
    if (dstTransitionDates !== null && day > dstTransitionDates.start && day < dstTransitionDates.end && userOffset !== dstTransitionDates.afterStartOffset) {
      userOffset += 1;
    } else if (dstTransitionDates !== null && day > dstTransitionDates.end && userOffset !== dstTransitionDates.afterEndOffset) {
      userOffset -= 1;
    }

    let schedules = [];
    const duration = team && team.settings && team.settings.duration ? team.settings.duration : 15;
    agents.forEach((x) => {
      let booked = bookedSchedules.filter((bs) => bs.agentId === x.id);
      let scheduleBreakdown = scheduleBreakdowns.filter((sc) => sc.agentId === x.id);
      const bufferTime = team && team.settings && team.settings.bufferTime ? parseInt(team.settings.bufferTime) : 0;
      let times = schedulingHelpers.getAgentDayAvailability(day, x, duration, booked, scheduleBreakdown, bufferTime, userOffset);

      times = schedulingHelpers.filterBookedSchedule(times, booked);
      times = schedulingHelpers.removeCurrentTimesFromSchedule(times);

      times.forEach((t) => {
        if (
          team &&
          team.settings &&
          team.settings.appointmentPerTimeSlot &&
          bookedSchedules.some((x) => x.schedule.startTime <= t.startTime && x.schedule.endTime >= t.endTime) &&
          bookedSchedules.filter((x) => x.schedule.startTime <= t.startTime && x.schedule.endTime >= t.endTime).length === team.settings.appointmentPerTimeSlot
        ) {
          return;
        }

        let index = schedules.find((s) => s.schedule.startTime === t.startTime);
        if (index) {
          index.agents.push(x);
        } else {
          schedules.push({ schedule: t, agents: [x] });
        }
      });
    });

    schedules.sort((a, b) => {
      if (a.schedule.startTime > b.schedule.startTime) return 1;
      if (a.schedule.startTime < b.schedule.startTime) return -1;
      return 0;
    });

    //if ((!schedules.length) /*&& !isTodayRef.current*/) return null;

    return (
      <DayScheduleContainer
        disabled={isButtonsDisabled}
        day={day}
        location={true}
        schedules={schedules}
        notAvailableMsg={"Sorry no agent is available to take the appointment."}
        onClick={onScheduleCallClick}
      />
    );
  };

  if (!agents) return null;

  let days = getDays();
  let scheduleBreakdowns = getBrokenDownSchedules();
  if (isInitialLoad.current && isToday) {
    isInitialLoad.current = false;
    let schedules = [];
    let day = new Date();
    agents.forEach((x) => {
      let booked = bookedSchedules.filter((bs) => bs.agentId === x.id);
      let scheduleBreakdown = scheduleBreakdowns.filter((sc) => sc.agentId === x.id);
      let times = schedulingHelpers.getAgentDayAvailability(day, x, 30, booked, scheduleBreakdown);
      if (!times) return;
      times.forEach((t) => {
        let index = schedules.find((s) => s.schedule.startTime === t.startTime);
        if (index) {
          index.agents.push(x);
        } else {
          schedules.push({ schedule: t, agents: [x] });
        }
      });
    });
    if (!schedules.length) {
      setIsToday(false);
      //isTodayRef.current = false;
    }
  }

  return (
    <div className="cts-ba-profile">
      <div className="cts-schedule">
        <h2>
          {content.common.scheduleAppointmentHeader}
          {content.common.scheduleAppointmentHeader2 ? (
            <>
              <br />
              {content.common.scheduleAppointmentHeader2}
            </>
          ) : null}
        </h2>
        <div>
          <TextToggle
            name="cts-schedule-name-toggle"
            r1Text={content.scheduleAppointment.thisWeekButton}
            r2Text={content.scheduleAppointment.todayButton}
            r3Text={content.scheduleAppointment.nextWeekButton}
            //value={!isTodayRef.current}
            value={selectedRadio}
            onChange={onTextToggled}
          />
        </div>
        <hr />
        {!isValid && (
          <>
            <div className="cts-error-text text-center">{content.scheduleAppointment.appointmentNotAvailable}</div>
            <hr />
          </>
        )}
        <div className="cts-schedule-days">
          {!days || days.length === 0 ? <div className="text-center">Sorry, no agent is available to take the appointment.</div> : days.map((day) => getDaySchedule(day))}
        </div>
      </div>
      {isInFrame() && (
        <div className="cts-footer-btn">
          <button onClick={onCancelClick} type="button" className="">
            {content.common.cancelButton}
          </button>
        </div>
      )}
    </div>
  );
};

export default LocationProfile;
